import { Controller } from '@hotwired/stimulus';

// The yearbook cover submission form (/yearbook_submissions/new?cover_only=yes):
// pick or drop a PDF, see page 1 drawn on a canvas, then initial, sign and date.
// Submit stays disabled until all three steps are done.
//
// The file input is the real control -- a drop writes into it, and a rejected
// file clears it -- so the form posts exactly what the preview shows. The PDF
// check here is a convenience; YearbookSubmission validates the content type.
//
// pdf.js is loaded from cdnjs on first use rather than bundled: it is ~1 MB and
// only this page needs it. If it cannot load, the file is still attached and
// submittable; only the preview is missing.

const MAX_PREVIEW_HEIGHT = 1600;
const MAX_PREVIEW_SCALE = 3;

let pdfjsPromise = null;

export function isPdf(file) {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
}

export function formatSize(bytes) {
  const kb = bytes / 1024;
  return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.round(kb)} KB`;
}

// PDF points to inches, without trailing zeros: 612 -> "8.5", 792 -> "11".
export function inches(points) {
  return (points / 72).toFixed(2).replace(/\.?0+$/, '');
}

export function resetPdfjsForTests() {
  pdfjsPromise = null;
}

export default class extends Controller {
  static targets = [
    'input', 'dropzone', 'empty', 'canvas', 'rendering', 'error', 'dragover',
    'filecard', 'filename', 'filemeta', 'chooseLabel',
    'initials', 'name', 'date', 'submit', 'checkFile', 'checkInitials', 'checkSign',
  ];

  static values = { libSrc: String, workerSrc: String };

  connect() {
    this.file = null;
    this.rendering = false;
    this.submitting = false;
    this.renderToken = 0;
    // Start fetching pdf.js now so the first preview is quick; a failure here
    // is reported when a file is chosen, not on page load.
    this.loadPdfjs().catch(() => {});
    this.update();
  }

  chosen() {
    this.load(this.inputTarget.files && this.inputTarget.files[0]);
  }

  dragOver(event) {
    event.preventDefault();
    this.dragoverTarget.hidden = false;
  }

  dragLeave(event) {
    if (!this.dropzoneTarget.contains(event.relatedTarget)) this.dragoverTarget.hidden = true;
  }

  drop(event) {
    event.preventDefault();
    this.dragoverTarget.hidden = true;
    const files = event.dataTransfer && event.dataTransfer.files;
    if (!files || !files[0]) return;

    try {
      this.inputTarget.files = files;
    } catch (_err) {
      // Very old browsers can't assign FileLists; the preview still works but
      // the input stays empty, so the "Cover uploaded" step stays open.
    }
    this.load(files[0]);
  }

  submit(event) {
    if (this.submitTarget.disabled) {
      event.preventDefault();
      return;
    }
    // A cover PDF can take a while to upload; a second click would create a
    // second production project.
    this.submitting = true;
    this.submitTarget.disabled = true;
    this.submitTarget.textContent = 'Uploading your cover…';
  }

  update() {
    if (this.submitting) return;

    const hasFile = !!this.file && this.inputHoldsFile();
    const hasInitials = this.initialsTarget.value.trim().length > 0;
    const hasSign = this.nameTarget.value.trim().length > 0 && !!this.dateTarget.value;
    const ready = hasFile && !this.rendering && hasInitials && hasSign;

    this.checkFileTarget.classList.toggle('is-done', hasFile);
    this.checkInitialsTarget.classList.toggle('is-done', hasInitials);
    this.checkSignTarget.classList.toggle('is-done', hasSign);
    this.submitTarget.disabled = !ready;
    this.submitTarget.textContent = ready ? 'Submit cover for print' : 'Complete the steps to submit';
  }

  // private

  inputHoldsFile() {
    return !!(this.inputTarget.files && this.inputTarget.files.length);
  }

  load(file) {
    if (!file) return;

    if (!isPdf(file)) {
      this.reset();
      this.showError(`“${file.name}” isn't a PDF. Please export your cover as a PDF and try again.`);
      return;
    }

    this.showError('');
    this.file = file;
    this.rendering = true;
    this.emptyTarget.hidden = true;
    this.canvasTarget.hidden = false;
    this.renderingTarget.hidden = false;
    this.filecardTarget.hidden = false;
    this.filenameTarget.textContent = file.name;
    this.filemetaTarget.textContent = `PDF · ${formatSize(file.size)}`;
    this.chooseLabelTarget.textContent = 'Choose a different PDF';
    this.update();
    return this.render(file);
  }

  reset() {
    this.file = null;
    this.rendering = false;
    this.renderToken += 1;
    this.inputTarget.value = '';
    this.emptyTarget.hidden = false;
    this.canvasTarget.hidden = true;
    this.renderingTarget.hidden = true;
    this.filecardTarget.hidden = true;
    this.chooseLabelTarget.textContent = 'Choose cover PDF';
    this.update();
  }

  async render(file) {
    this.renderToken += 1;
    const token = this.renderToken;

    let lib;
    try {
      lib = await this.loadPdfjs();
    } catch (_err) {
      if (token !== this.renderToken) return;
      this.finishRendering();
      this.showError("Preview isn't available right now, but your PDF is attached and ready to submit.");
      return;
    }

    try {
      const data = new Uint8Array(await file.arrayBuffer());
      const pdf = await lib.getDocument({ data }).promise;
      const page = await pdf.getPage(1);
      if (token !== this.renderToken) return;

      const base = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: Math.min(MAX_PREVIEW_HEIGHT / base.height, MAX_PREVIEW_SCALE) });
      this.canvasTarget.width = Math.round(viewport.width);
      this.canvasTarget.height = Math.round(viewport.height);
      await page.render({ canvasContext: this.canvasTarget.getContext('2d'), viewport }).promise;
      if (token !== this.renderToken) return;

      const pages = pdf.numPages === 1 ? '1 page' : `${pdf.numPages} pages`;
      this.filemetaTarget.textContent =
        `PDF · ${formatSize(file.size)} · ${inches(base.width)} × ${inches(base.height)} in · ${pages}`;
      if (pdf.numPages > 1) {
        this.showError(`This PDF has ${pdf.numPages} pages. Only the first page is shown; your cover should be a single page.`);
      }
      this.finishRendering();
    } catch (_err) {
      if (token !== this.renderToken) return;
      this.reset();
      this.showError("We couldn't read that PDF. It may be damaged or password protected. Try exporting it again.");
    }
  }

  finishRendering() {
    this.rendering = false;
    this.renderingTarget.hidden = true;
    this.update();
  }

  showError(message) {
    this.errorTarget.textContent = message || '';
    this.errorTarget.hidden = !message;
  }

  loadPdfjs() {
    if (window.pdfjsLib) return Promise.resolve(this.configure(window.pdfjsLib));

    if (!pdfjsPromise) {
      pdfjsPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = this.libSrcValue;
        script.async = true;
        script.onload = () => (window.pdfjsLib ? resolve(window.pdfjsLib) : reject(new Error('pdf.js missing')));
        script.onerror = () => reject(new Error('pdf.js failed to load'));
        document.head.appendChild(script);
      }).catch((err) => {
        // Let the next file try again rather than caching the failure.
        pdfjsPromise = null;
        throw err;
      });
    }
    return pdfjsPromise.then((lib) => this.configure(lib));
  }

  configure(lib) {
    lib.GlobalWorkerOptions.workerSrc = this.workerSrcValue;
    return lib;
  }
}
